import * as React from "react";
import {VeBoxTextInput} from "./VeBoxTextInput";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {TextInputValues} from "../user-details-sections/UserInfo";
import {useDebounce} from "@vebox/etui-core";
import {isValidEmailAddress} from "../../account/AccountUtils";
import {isHTMLTagSafe} from "../../Utils/Utils";

declare var AppTranslations: AppTranslations;

export type ValueType = string | number | readonly string []


type UserInfoFieldProps = {
    value: ValueType;
    defaultValue: ValueType;
    label: string,
    path: TextInputValues,
    readOnly: false,
    maxValueLength?: number
    getErrorMessage: (errorMessage) => void
    onChange: React.ChangeEventHandler<HTMLInputElement>
    onCancel: () => void
} | {
    defaultValue: string | number | readonly string [];
    label: string,
    readOnly: true
}

interface UserInfoErrors {
    emailTaken: boolean;
    emailFormatError: boolean;
    textFormatError: boolean;
    lengthError: boolean;
}

enum UserInfoErrorsTypes {
    emailTaken = "emailTaken",
    emailFormatError = "emailFormatError",
    textFormatError = "textFormatError",
    lengthError = "lengthError"

}

const containError = (errors: UserInfoErrors) => {
    return errors.emailTaken || errors.emailFormatError || errors.textFormatError || errors.lengthError;
}

export const UserInfoField: React.FC<UserInfoFieldProps> = (props) => {

    const [userInfoErrors, setUserInfoErrors] = React.useState<UserInfoErrors>(
        {
            emailTaken: false,
            emailFormatError: false,
            textFormatError: false,
            lengthError: false
        });


    const [textValue, setTextValue] = React.useState<ValueType>((props.readOnly === false) ? props?.value : undefined);
    const [eventToShare, setEventToShare] = useDebounce<React.ChangeEvent<HTMLInputElement>>(undefined, 200);


    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [emailValue, setEmailValue] = useDebounce<string>("", 200);

    const updateError = (errorType: UserInfoErrorsTypes, value: boolean) => {
        setUserInfoErrors((prev) => {
            return {...prev, [errorType]: value}
        })
    }

    const resetError = (errorType: UserInfoErrorsTypes) => {
        setUserInfoErrors((prev) => {
            return {...prev, [errorType]: false}
        })
    }

    const resetAllErrors = () => {
        setUserInfoErrors({
            emailTaken: false,
            emailFormatError: false,
            textFormatError: false,
            lengthError: false
        })
    }

    const manageErrorMessage = () => {
        switch (true) {
            case userInfoErrors.lengthError: {
                setErrorMessage(AppTranslations.errorMessages.tooManyCharacters)
                if (props.readOnly === false) {
                    props?.getErrorMessage(AppTranslations.errorMessages.tooManyCharacters)
                }
                break;
            }
            case userInfoErrors.emailFormatError: {
                setErrorMessage(AppTranslations.userCreationProcess.emailInvalidFormat)
                if (props.readOnly === false) {
                    props?.getErrorMessage(AppTranslations.userCreationProcess.emailInvalidFormat)
                }
                break;
            }
            case userInfoErrors.textFormatError: {
                setErrorMessage(AppTranslations.errorMessages.charactersNotAllowedInserted)
                if (props.readOnly === false) {
                    props?.getErrorMessage(AppTranslations.errorMessages.charactersNotAllowedInserted)
                }
                break;
            }
            case userInfoErrors.emailTaken: {
                setErrorMessage(AppTranslations.userCreationProcess.emailTaken)
                if (props.readOnly === false) {
                    props?.getErrorMessage(AppTranslations.userCreationProcess.emailTaken)
                }
                break;
            }
            default: {
                setErrorMessage("")
                if (props.readOnly === false) {
                    props?.getErrorMessage("");
                }
            }
        }
    }

    const onChangeFunction = (e) => {
        if (userInfoErrors.emailTaken) {
            resetError(UserInfoErrorsTypes.emailTaken);
        }
        if (props.readOnly === false) {
            if (props.path === TextInputValues.email) {
                emailValidation(e)
                setEmailValue(e.target.value)
            }
            valueLengthValidation(e)
            HTMLValidation(e);
            setTextValue(e.target.value)
            setEventToShare(e)
        }
    }

    const onCancel = (event: React.MouseEvent<HTMLSpanElement>, defValue: string | number | readonly string[]) => {
        resetAllErrors()
        if (props.readOnly === false) {
            props.onCancel()
        }
    }

    const emailValidation = (e) => {
        updateError(UserInfoErrorsTypes.emailFormatError, !isValidEmailAddress(e.target.value));
    }

    const valueLengthValidation = (e) => {
        updateError(UserInfoErrorsTypes.lengthError,
            (props.readOnly === false) && e.target.value.length > props.maxValueLength)
    }

    const HTMLValidation = (e) => {
        updateError(UserInfoErrorsTypes.textFormatError, (props.readOnly === false) && !isHTMLTagSafe(e.target.value))
    }

    React.useEffect(() => {
        manageErrorMessage();
    }, [userInfoErrors])

    React.useEffect(() => {
        if (emailValue && emailValue !== props.defaultValue) {
            if (!userInfoErrors.emailFormatError && ((props.readOnly === false) ? props?.value : props.defaultValue) !== emailValue) {
                getEmailValidity(emailValue)
                    .then((response) => {
                        updateError(UserInfoErrorsTypes.emailTaken, response.emailUsedUp)
                    })
                    .catch((e) => {
                        setErrorMessage(e.errorType)
                    });
            } else {
                updateError(UserInfoErrorsTypes.emailTaken, false);
            }
        }
    }, [emailValue])

    React.useEffect(() => {
        if (props.readOnly === false && eventToShare) {
            props.onChange(eventToShare)
        }
    }, [eventToShare])

    React.useEffect(() => {
        if (props.readOnly === false && props?.value) {
            setTextValue(props?.value)
        }
    }, [(props.readOnly === false) ? props?.value : props.defaultValue])


    return <VeBoxTextInput
        styles={{input: {boxSizing: "border-box"}, root: {pb: "1px"}}}
        className={"user-detail-text-input"}
        name={props.label.toLowerCase()}
        label={props.label}
        defaultValue={props.defaultValue}
        value={textValue}
        onChange={onChangeFunction}
        onCancel={onCancel}
        withOperationAddon={!containError(userInfoErrors)}
        readOnly={props.readOnly}
        canSubmit={false}
        error={containError(userInfoErrors) ? {errorMessage} : undefined}
    />
}

interface ValidityResponse {
    emailUsedUp: boolean;
    usernameUsedUp: boolean;
    parentCompanyGuids: string[];
    type: string;
}


function getEmailValidity(email: string) {
    return new Promise<ValidityResponse>((res, rej) => {
        RestDataLoader.loadData(
            "support/registration?email=" + email,
            {},
            res,
            rej,
            false
        );
    });
}